import { useCallback, useEffect, useMemo, useState } from "react";
import { Expand } from "lucide-react";
import {
  normalizeSlotMix,
  type InstrumentSlot,
} from "../../../../../src/lib/audio";
import { useStoreSelector } from "../../../../../src/lib/store";
import { getLabActions } from "../../labActions";
import {
  clampInstrumentWidth,
  INSTRUMENT_WIDTH_MAX,
  INSTRUMENT_WIDTH_MIN,
} from "../../lib/dockSizes";
import { useDockResize } from "../../lib/useDockResize";
import { projectStore } from "../../store/projectStore";
import { patchUi, uiStore } from "../../store/uiStore";
import { ParamKnob } from "../ParamKnob";
import {
  INSTRUMENT_KIND_OPTIONS,
  instrumentEditors,
  resolveInstrumentKind,
  type InstrumentKind,
} from "./instrumentEditors";
import {
  dockAsideBase,
  dockPanel,
  dockPanelClose,
  dockPanelHeader,
  dockPanelTitle,
  synthDockChrome,
} from "./dockChrome";

/**
 * Left-rail slot inspector. Channel follows the tracker cursor;
 * strip has track name / mute·solo / volume / pan; body from `instrumentEditors[kind]`.
 * Drag the right edge to resize.
 */
export function InstrumentDock() {
  const slot = useStoreSelector(uiStore, s => s.instrumentSlot);
  const storedWidth = useStoreSelector(uiStore, s => s.instrumentWidth);
  const instruments = useStoreSelector(projectStore, s => s.instruments);
  const slotMixes = useStoreSelector(projectStore, s => s.slotMix);
  const clip = useStoreSelector(projectStore, s => s.clip);
  const activePatternId = useStoreSelector(uiStore, s => s.activePatternId);
  const trackMuted = useStoreSelector(uiStore, s => s.trackMuted);
  const trackSolo = useStoreSelector(uiStore, s => s.trackSolo);
  const open = slot !== null;
  const activeSlot: InstrumentSlot = slot ?? 0;
  const kind = resolveInstrumentKind(instruments[activeSlot]?.kind);
  const Editor = instrumentEditors[kind];
  const mix = normalizeSlotMix(slotMixes[activeSlot]);
  const muted = trackMuted[activeSlot] === true;
  const solo = trackSolo[activeSlot] === true;

  const commitWidth = useCallback((next: number) => {
    patchUi({ instrumentWidth: next });
    getLabActions()?.persistPanels({ instrumentWidth: next });
  }, []);

  const {
    size: width,
    dragging,
    onPointerDown,
    onPointerMove,
    onPointerUp,
    nudge,
  } = useDockResize({
    storedSize: storedWidth,
    clamp: clampInstrumentWidth,
    onCommit: commitWidth,
    axis: "x",
    edge: "end",
  });

  const trackName = useMemo(() => {
    const pattern =
      clip.patterns.find(p => p.id === activePatternId)
      ?? clip.patterns[0];
    const name = pattern?.tracks[activeSlot]?.name?.trim();
    return name || `I${activeSlot + 1}`;
  }, [clip, activePatternId, activeSlot]);

  const [nameDraft, setNameDraft] = useState(trackName);
  useEffect(() => {
    setNameDraft(trackName);
  }, [trackName, activeSlot]);

  function close(): void {
    getLabActions()?.closeInstrument?.();
  }

  function onKindChange(next: InstrumentKind): void {
    const current = resolveInstrumentKind(instruments[activeSlot]?.kind);
    if (next === current) {
      return;
    }
    getLabActions()?.setInstrumentKind?.(activeSlot, next);
  }

  function commitName(): void {
    const next = nameDraft.trim() || `I${activeSlot + 1}`;
    setNameDraft(next);
    if (next !== trackName) {
      getLabActions()?.renameTrack?.(activeSlot, next);
    }
  }

  return (
    <aside
      id="synth-dock"
      className={`synth-dock relative ${dockAsideBase} ${synthDockChrome} ${
        open ? "flex" : "hidden"
      } ${dragging ? "select-none" : ""}`}
      style={{ width }}
    >
      <div
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize Instrument"
        aria-valuemin={INSTRUMENT_WIDTH_MIN}
        aria-valuemax={INSTRUMENT_WIDTH_MAX}
        aria-valuenow={width}
        tabIndex={0}
        className={[
          "absolute top-0 right-0 bottom-0 z-20 w-1.5 cursor-col-resize touch-none",
          "bg-transparent hover:bg-lab-accent/35 active:bg-lab-accent/50",
          "max-[720px]:hidden",
          dragging ? "bg-lab-accent/40" : "",
        ].join(" ")}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onKeyDown={event => {
          const step = event.shiftKey ? 32 : 16;
          if (event.key === "ArrowRight") {
            event.preventDefault();
            nudge(step);
          } else if (event.key === "ArrowLeft") {
            event.preventDefault();
            nudge(-step);
          }
        }}
      />
      <div
        className={`synth-panel ${dockPanel}`}
        tabIndex={0}
        role="region"
        aria-label="Instrument editor"
      >
        <div className={`synth-panel-header ${dockPanelHeader}`}>
          <div className={`synth-panel-title ${dockPanelTitle}`}>
            <span className="mr-1.5 rounded border border-lab-line px-1 py-0.5 text-[10px] text-lab-muted">
              I{activeSlot + 1}
            </span>
            Instrument
          </div>
          <div className="flex shrink-0 items-center gap-[0.3rem]">
            <select
              key={activeSlot}
              className="rounded border border-lab-line bg-lab-panel px-1.5 py-0.5 text-[11px] text-slate-200"
              value={kind}
              aria-label="Instrument type"
              onChange={event => onKindChange(resolveInstrumentKind(event.target.value))}
            >
              {INSTRUMENT_KIND_OPTIONS.map(option => (
                <option key={option.kind} value={option.kind}>
                  {option.label}
                </option>
              ))}
            </select>
            {kind !== "sf2" && kind !== "externalMidi" && (
              <button
                type="button"
                title="Toggle Author (⌘/Ctrl+I)"
                aria-label="Toggle Author"
                className="inline-flex h-[1.55rem] w-[1.55rem] items-center justify-center rounded border border-[color-mix(in_srgb,var(--accent)_45%,transparent)] bg-[rgba(61,139,253,0.18)] text-slate-200 hover:border-lab-accent hover:bg-[rgba(61,139,253,0.28)] hover:text-white"
                onClick={() => getLabActions()?.openInstrumentAuthor?.()}
              >
                <Expand className="size-4 shrink-0" strokeWidth={2.25} aria-hidden />
              </button>
            )}
            <button type="button" className={`synth-panel-close ${dockPanelClose}`} onClick={close}>
              Close
            </button>
          </div>
        </div>

        <div className="synth-track-strip flex shrink-0 flex-col gap-2 border-b border-white/[0.06] bg-lab-panel-bar px-2 py-2">
          <div className="flex items-center gap-2">
            <label className="flex min-w-0 flex-1 items-center gap-2 text-[11px] text-lab-muted">
              <span className="w-10 shrink-0">Name</span>
              <input
                className="min-w-0 grow rounded border border-lab-line bg-lab-panel px-2 py-1 text-[12px] text-slate-200"
                value={nameDraft}
                aria-label="Track name"
                onChange={event => setNameDraft(event.target.value)}
                onBlur={commitName}
                onKeyDown={event => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    (event.target as HTMLInputElement).blur();
                  }
                }}
              />
            </label>
            <div className="flex shrink-0 gap-1">
              <button
                type="button"
                title="Solo"
                aria-label={`Solo instrument ${activeSlot + 1}`}
                aria-pressed={solo}
                className={`inline-grid h-7 w-7 place-items-center rounded border p-0 text-[11px] leading-none font-bold ${
                  solo
                    ? "border-[#e6b35a] bg-[#e6b35a] text-slate-900"
                    : "border-lab-line bg-lab-panel text-slate-500 hover:border-white/30 hover:text-slate-200"
                }`}
                onClick={() => getLabActions()?.toggleSolo?.(activeSlot)}
              >
                S
              </button>
              <button
                type="button"
                title="Mute"
                aria-label={`Mute instrument ${activeSlot + 1}`}
                aria-pressed={muted}
                className={`inline-grid h-7 w-7 place-items-center rounded border p-0 text-[11px] leading-none font-bold ${
                  muted
                    ? "border-slate-400 bg-slate-400 text-slate-900"
                    : "border-lab-line bg-lab-panel text-slate-500 hover:border-white/30 hover:text-slate-200"
                }`}
                onClick={() => getLabActions()?.toggleMute?.(activeSlot)}
              >
                M
              </button>
            </div>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <ParamKnob
              label="Vol"
              min={0}
              max={1}
              step={0.01}
              value={mix.volume}
              defaultValue={1}
              density="compact"
              learnPath={`/track/${activeSlot}/mix/volume`}
              onChange={volume => getLabActions()?.setSlotMix?.(activeSlot, { volume })}
            />
            <ParamKnob
              label="Pan"
              min={-1}
              max={1}
              step={0.01}
              value={mix.pan}
              defaultValue={0}
              density="compact"
              learnPath={`/track/${activeSlot}/mix/pan`}
              format={v => {
                if (Math.abs(v) < 0.02) {
                  return "C";
                }
                return v < 0 ? `L${Math.round(Math.abs(v) * 100)}` : `R${Math.round(v * 100)}`;
              }}
              onChange={pan => getLabActions()?.setSlotMix?.(activeSlot, { pan })}
            />
          </div>
        </div>

        {/* Remount editor when kind changes so local pad/octave state resets. */}
        <Editor key={`${activeSlot}-${kind}`} slot={activeSlot} />
      </div>
    </aside>
  );
}
